import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { api, ApiError } from "../lib/api.js";
import { logApiError } from "../lib/apiErrors.js";
import { StripeEmbeddedCheckout } from "./StripeEmbeddedCheckout.js";
import { StoneCardForm } from "./StoneCardForm.js";
import { EMPTY_STONE_CARD, invalidStoneCardField, tokenizeStoneCard, type StoneCardFields } from "../lib/stoneCard.js";

type Outcome = "paid" | "no_open_invoice" | "already_paid" | "declined" | "processing" | "card_refused";

/** Outcomes where the new card did not take the renewal: offer another card. */
const RETRY_OUTCOMES: Outcome[] = ["declined", "card_refused"];

type Started =
  | { provider: "stripe"; session_id: string; client_secret: string; publishable_key: string }
  | { provider: "stone"; stone_public_key: string };

/**
 * "Atualizar cartão" (PIT-459). Stripe collects the card in its embedded form (setup mode, nothing
 * charged by the form itself); Stone takes it in our form, tokenized in the browser. Either way the
 * server makes it the subscription's card and charges a declined renewal on it once.
 */
export function CardUpdatePanel({
  token,
  pastDue,
  onBack,
  onDone,
}: {
  token: string;
  pastDue: boolean;
  onBack: () => void;
  /** After a successful update, so the page reloads the subscription. */
  onDone: (outcome: Outcome) => void;
}) {
  const { t } = useTranslation();
  const [started, setStarted] = useState<Started | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ outcome: Outcome } | null>(null);
  // Stripe: the session completed but /apply failed. The embedded form cannot run that session
  // again, so the retry re-sends it (the server is idempotent per session).
  const [stripeSessionToRetry, setStripeSessionToRetry] = useState<string | null>(null);
  const [stoneCard, setStoneCard] = useState<StoneCardFields>(EMPTY_STONE_CARD);

  useEffect(() => {
    let cancelled = false;
    void api<Started>("/api/integrated/card-update/start", { method: "POST", body: JSON.stringify({ token }) })
      .then((r) => {
        if (!cancelled) setStarted(r);
      })
      .catch((err) => {
        logApiError("card_update.start", err);
        if (!cancelled) setError(t("integrated.cardUpdate.startFailed"));
      });
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  async function apply(body: { session_id?: string; card_token?: string }) {
    setSubmitting(true);
    setError(null);
    try {
      const r = await api<{ outcome: Outcome }>("/api/integrated/card-update/apply", {
        method: "POST",
        body: JSON.stringify({ token, ...body }),
      });
      setStripeSessionToRetry(null);
      setResult({ outcome: r.outcome });
      if (!RETRY_OUTCOMES.includes(r.outcome)) onDone(r.outcome);
    } catch (err) {
      logApiError("card_update.apply", err);
      if (body.session_id) setStripeSessionToRetry(body.session_id);
      setError(
        err instanceof ApiError && err.status === 409
          ? t("integrated.cardUpdate.notAvailable")
          : t("integrated.cardUpdate.applyFailed"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function submitStone(publicKey: string) {
    const badField = invalidStoneCardField(stoneCard);
    if (badField) return setError(t(`checkout.stone.errors.${badField}`));
    setSubmitting(true);
    setError(null);
    let cardToken: string;
    try {
      cardToken = await tokenizeStoneCard(publicKey, stoneCard);
    } catch (err) {
      logApiError("card_update.stone.tokenize", err);
      setError(t("checkout.stone.errors.tokenize"));
      setSubmitting(false);
      return;
    }
    await apply({ card_token: cardToken });
  }

  return (
    <div className="card-glass p-6 lg:p-8 text-[color:var(--text-primary)] space-y-4">
      <button
        type="button"
        className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-[color:var(--text-primary)]"
        onClick={onBack}
      >
        <ArrowLeft className="w-4 h-4" aria-hidden />
        {t("integrated.manageTitle")}
      </button>
      <h1 className="text-xl font-semibold" style={{ letterSpacing: "-0.02em" }}>
        {t("integrated.cardUpdate.title")}
      </h1>

      {result ? (
        <div className="space-y-3">
          <p
            className={`text-sm leading-relaxed ${RETRY_OUTCOMES.includes(result.outcome) ? "text-danger" : "text-ink-2"}`}
          >
            {t(`integrated.cardUpdate.outcome.${result.outcome}`)}
          </p>
          {RETRY_OUTCOMES.includes(result.outcome) ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setResult(null);
                setStoneCard(EMPTY_STONE_CARD);
                setStarted(null);
                setError(null);
                void api<Started>("/api/integrated/card-update/start", {
                  method: "POST",
                  body: JSON.stringify({ token }),
                })
                  .then(setStarted)
                  .catch(() => setError(t("integrated.cardUpdate.startFailed")));
              }}
            >
              {t("integrated.cardUpdate.tryAnother")}
            </button>
          ) : (
            <button type="button" className="btn-primary" onClick={onBack}>
              {t("integrated.cardUpdate.backToSubscription")}
            </button>
          )}
        </div>
      ) : (
        <>
          <p className="text-sm text-ink-2 leading-relaxed">
            {pastDue ? t("integrated.cardUpdate.bodyPastDue") : t("integrated.cardUpdate.body")}
          </p>
          {error && <p className="text-sm text-danger">{error}</p>}
          {!started && !error && <p className="text-sm text-ink-2">{t("common.loading")}</p>}
          {started?.provider === "stripe" && stripeSessionToRetry && !submitting && (
            <button type="button" className="btn-primary" onClick={() => void apply({ session_id: stripeSessionToRetry })}>
              {t("integrated.cardUpdate.retrySave")}
            </button>
          )}
          {started?.provider === "stripe" && !stripeSessionToRetry && !submitting && (
            <StripeEmbeddedCheckout
              publishableKey={started.publishable_key}
              clientSecret={started.client_secret}
              onComplete={() => void apply({ session_id: started.session_id })}
            />
          )}
          {started?.provider === "stripe" && submitting && (
            <p className="text-sm text-ink-2">{t("integrated.processing")}</p>
          )}
          {started?.provider === "stone" && (
            <div className="space-y-4">
              <StoneCardForm value={stoneCard} onChange={setStoneCard} disabled={submitting} />
              <button
                type="button"
                className="btn-primary w-full"
                disabled={submitting}
                onClick={() => void submitStone(started.stone_public_key)}
              >
                {submitting ? t("integrated.processing") : t("integrated.cardUpdate.save")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
